<template>
  <div class="nc-iframe-container" :style="styleVars">
    <div v-if="blur" class="tw-absolute tw-inset-0 tw-z-30 tw-bg-white/20"></div>
    <iframe
      :src="embedSrc"
      class="nc-iframe"
      :class="iframeClass"
      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: var(--rounded)"
      allowfullscreen
      title="Video player"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
      referrerpolicy="strict-origin-when-cross-origin"
    >
    </iframe>
    <div v-if="blur" class="nc-overlay-item-on-image">
      <slot name="overlay-item">
        <div class="pre-market-property-badge tw-text-center">
          <h3 v-html="preMarketBlurText" class="tw-text-white"></h3>
        </div>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
export default defineNuxtComponent({
  name: 'LibraryIframe',

  props: {
    src: {
      required: true,
      type: String,
    },

    hRatio: {
      required: false,
      type: Number,
      default: 2,
    },
    wRatio: {
      required: false,
      type: Number,
      default: 3,
    },

    blur: {
      required: false,
      type: String,
      default: null,
    },

    preMarketBlurText: {
      required: false,
      type: String,
      default: 'Pre-Market<br />Property',
    },

    iframeClass: {
      required: false,
      type: String,
      default: '',
    },
  },

  computed: {
    embedSrc(): string | undefined {
      if (this.resource === 'youtube') {
        function getYouTubeVideoId(url: string): string | null {
          // Regular expression to match the video ID in various YouTube URL formats
          const regExp = /^.*(youtu\.be\/|v\/|\/u\/\w\/|embed\/|watch\?v=|\&v=|watch\?vi=)([^#\&\?]*).*/;
          const match = url.match(regExp);
          if (match && match[2].length === 11) {
            return match[2];
          } else {
            return null;
          }
        }

        const id = getYouTubeVideoId(this.src);
        if (id) {
          return `https://www.youtube.com/embed/${id}`;
        }
        return undefined;
      }

      if (this.resource === 'vimeo') {
        function getVimeoEmbedUrl(url: string): string | null {
          // Pattern 1: player.vimeo.com/video/ID?h=HASH (and other params)
          // Example: https://player.vimeo.com/video/1083498293?h=b4ca02a2ce&badge=0
          let match = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\?.*?(?:h=([a-zA-Z0-9_-]+)))?/i);
          if (match && match[1]) {
            const videoId = match[1];
            const privacyHash = match[2]; // This will be the value of 'h' or undefined
            if (privacyHash) {
              return `https://player.vimeo.com/video/${videoId}?h=${privacyHash}`;
            }
            return `https://player.vimeo.com/video/${videoId}`;
          }

          // Pattern 2: vimeo.com/ID or vimeo.com/ID/HASH or vimeo.com/path/to/ID or vimeo.com/path/to/ID/HASH
          // Example: https://vimeo.com/1083498293/b4ca02a2ce
          // Example: https://vimeo.com/123456789
          // Example: https://vimeo.com/channels/staffpicks/123456789
          match = url.match(/vimeo\.com\/(?:(?:[^\/]+\/)*)?(\d+)(?:\/([a-zA-Z0-9_-]+))?/i);
          if (match && match[1]) {
            const videoId = match[1];
            const pathHash = match[2]; // This will be the HASH part from vimeo.com/ID/HASH, or undefined
            if (pathHash) {
              return `https://player.vimeo.com/video/${videoId}?h=${pathHash}`;
            }
            return `https://player.vimeo.com/video/${videoId}`;
          }

          return null;
        }

        const vimeoEmbedUrl = getVimeoEmbedUrl(this.src);
        if (vimeoEmbedUrl) {
          return vimeoEmbedUrl;
        }
        return undefined;
      }

      return this.src; // Fallback for other resource types
    },

    resource(): 'youtube' | 'vimeo' | null {
      if (this.src) {
        if (this.src.includes('youtu.be') || this.src.includes('youtube')) {
          return 'youtube';
        }
        if (this.src.includes('vimeo')) {
          return 'vimeo';
        }
      }
      return null;
    },

    imageRatio() {
      return (this.hRatio / this.wRatio) * 100;
    },

    styleVars() {
      return {
        '--nc-iframe-ratio': `${this.imageRatio}%`,
        '--nc-mobile-image-ratio': `${this.imageRatio}%`,
        '--nc-iframe-blur-filter': `blur(${this.blur})`,
      };
    },
  },
});
</script>

<style scoped>
.nc-iframe-container {
  position: relative;
  width: 100%; /* This makes the container responsive */
  padding-bottom: var(--nc-iframe-ratio); /* This maintains aspect ratio */
}

@media (max-width: 1023px) {
  .nc-iframe-container {
    padding-bottom: var(--nc-mobile-image-ratio); /* This maintains aspect ratio on mobile */
  }
}

.nc-iframe-container .nc-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* This ensures the image covers the container */
  filter: var(--nc-iframe-blur-filter);
}

.nc-overlay-item-on-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
}
</style>
